import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Profile, Certification, InsurancePolicy, supabase } from '../../lib/supabase';
import {
  Share2,
  Download,
  Link as LinkIcon,
  QrCode,
  Smartphone,
  Check,
  Copy,
  Mail,
  ExternalLink
} from 'lucide-react';
import {
  generateProfileURL,
  downloadVCard,
  shareViaWebShare,
  copyToClipboard,
  generateQRCode,
  downloadQRCode,
  downloadAppleWalletPass
} from '../../lib/shareService';

interface ShareProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: Profile;
  certifications: Certification[];
  insurancePolicies: InsurancePolicy[];
  email?: string;
}

type ShareMethod = 'link' | 'vcard' | 'apple_wallet' | 'qr_code' | 'email';

export const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  certifications,
  insurancePolicies,
  email
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  const profileUrl = profile.public_slug ? generateProfileURL(profile.public_slug) : '';

  useEffect(() => {
    if (isOpen && profileUrl) {
      generateQR();
    }
  }, [isOpen, profileUrl]);

  const generateQR = async () => {
    if (!profileUrl) return;
    setIsGeneratingQR(true);
    try {
      const dataUrl = await generateQRCode(profileUrl, 400);
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const trackShare = async (method: ShareMethod) => {
    try {
      await supabase.from('profile_shares').insert({
        profile_id: profile.id,
        share_method: method
      });
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const handleCopyLink = async () => {
    if (!profileUrl) return;
    try {
      await copyToClipboard(profileUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
      await trackShare('link');
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleWebShare = async () => {
    if (!profileUrl) return;
    const shareData = {
      title: `${profile.full_name} - Professional Contractor`,
      text: `Check out my professional contractor profile`,
      url: profileUrl
    };

    const shared = await shareViaWebShare(shareData);
    if (shared) {
      await trackShare('link');
    } else {
      await handleCopyLink();
    }
  };

  const handleDownloadVCard = () => {
    downloadVCard({
      profile,
      certifications,
      insurancePolicies,
      email
    });
    trackShare('vcard');
  };

  const handleDownloadQR = async () => {
    if (!profileUrl || !profile.full_name) return;
    await downloadQRCode(
      profileUrl,
      `${profile.full_name.replace(/\s+/g, '_')}_QR`
    );
    await trackShare('qr_code');
  };

  const handleDownloadWalletPass = () => {
    downloadAppleWalletPass({
      profile,
      email,
      certifications
    });
    trackShare('apple_wallet');
  };

  const handleEmailShare = () => {
    if (!profileUrl) return;
    const subject = encodeURIComponent(`${profile.full_name} - Professional Contractor Profile`);
    const body = encodeURIComponent(
      `Hi,\n\nI wanted to share my professional contractor profile with you:\n\n${profileUrl}\n\nBest regards,\n${profile.full_name}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    trackShare('email');
  };

  if (!profile.public_slug || !profile.public_profile_enabled) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Share Profile">
        <div className="p-6 text-center">
          <p className="text-slate-600 mb-4">
            Public profile sharing is currently disabled. Enable it in your settings to share your profile.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Your Profile">
      <div className="p-6 space-y-6">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 mr-4">
              <p className="text-xs text-slate-500 mb-1">Your Profile Link</p>
              <p className="text-sm text-slate-900 break-all font-mono">{profileUrl}</p>
            </div>
            <button
              onClick={handleCopyLink}
              className="flex-shrink-0 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center space-x-2"
            >
              {copiedLink ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm">Copy</span>
                </>
              )}
            </button>
          </div>
          <button
            onClick={handleWebShare}
            className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center space-x-2"
          >
            <Share2 className="w-4 h-4" />
            <span>Share via AirDrop, Messages, Email, etc.</span>
          </button>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">Download Options</h3>

          <button
            onClick={handleDownloadVCard}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                <Download className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-900">Contact Card (vCard)</p>
                <p className="text-xs text-slate-500">Save to phone contacts</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </button>

          <button
            onClick={handleDownloadWalletPass}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-slate-100 transition-colors">
                <Smartphone className="w-5 h-5 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-900">Apple Wallet Pass</p>
                <p className="text-xs text-slate-500">Add to Apple Wallet</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </button>

          <button
            onClick={handleEmailShare}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-between group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-900">Email Profile</p>
                <p className="text-xs text-slate-500">Send via email</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </button>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">QR Code</h3>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            {isGeneratingQR ? (
              <div className="aspect-square flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
              </div>
            ) : qrCodeDataUrl ? (
              <div className="space-y-3">
                <img
                  src={qrCodeDataUrl}
                  alt="Profile QR Code"
                  className="w-full rounded-lg"
                />
                <p className="text-xs text-slate-500 text-center">
                  Scan this code to view your profile
                </p>
                <button
                  onClick={handleDownloadQR}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download QR Code</span>
                </button>
              </div>
            ) : (
              <div className="aspect-square flex items-center justify-center">
                <p className="text-slate-500">Unable to generate QR code</p>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center">
            Your profile has been viewed {profile.profile_views || 0} times
          </p>
        </div>
      </div>
    </Modal>
  );
};
